import { useEffect, useState } from "react";

import Typography from "@mui/material/Typography";

import type { ConnectedCalendarProvider } from "@app/common/auth/api";
import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import type { CalendarProviderId } from "@app/common/types/providerTypes";

import ConfirmationModal, {
  ConfirmationModalProps,
} from "@rui/ConfirmationModal";
import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";

interface SelectDefaultCalendarModalProps
  extends Omit<ConfirmationModalProps, "children" | "onConfirm"> {
  calendarProviders: ConnectedCalendarProvider[];
  onConfirm: (providerId: CalendarProviderId) => void;
}

const SelectDefaultCalendarModal = ({
  calendarProviders,
  onConfirm,
  ...modalProps
}: SelectDefaultCalendarModalProps) => {
  const [selectedProvider, setSelectedProvider] =
    useState<CalendarProviderId | null>(null);

  useEffect(() => {
    if (modalProps.open) return;
    setSelectedProvider(null);
  }, [modalProps.open]);

  const handleConfirm = () => {
    if (!selectedProvider) return;
    onConfirm(selectedProvider);
  };

  const dropdownOptions = calendarProviders.map((provider) => ({
    value: provider.providerId,
    label: `${CALENDAR_PROVIDERS_BY_ID[provider.providerId].displayName}${
      provider.providerEmail ? ` (${provider.providerEmail})` : ""
    }`,
  }));

  return (
    <ConfirmationModal
      {...modalProps}
      title="Select default calendar"
      size="md"
      onConfirm={handleConfirm}
      confirmProps={{ disabled: selectedProvider == null }}
    >
      <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
        Your default calendar will be used for availability and scheduling
        meetings when your scheduling link is shared with others.
      </Typography>
      <Dropdown
        name="rss-links-default-calendar"
        selectedItem={getSelectedFromOptions(dropdownOptions, selectedProvider)}
        placeholder="Select one"
        onChange={(event) =>
          setSelectedProvider(event.target.value as CalendarProviderId)
        }
      >
        {dropdownOptions.map(({ value, label }) => (
          <DropdownMenuItem key={value} value={value} label={label} />
        ))}
      </Dropdown>
    </ConfirmationModal>
  );
};

export default SelectDefaultCalendarModal;
